/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

// Documented losses limit
const DOCUMENTED_LOSSES_LIMIT = 5000;
const DOCUMENTED_LOSSES_ERROR_CONTAINER_ID = "documented-losses-error";
const DOCUMENTED_LOSSES_AMOUNT_INPUT_SELECTOR = 'input[id*="amount__"]';

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    // custom code goes here
    const template = document.querySelector("#general_instructions");
    document.querySelector("#general_instructions_placeholder").innerHTML =
        template.innerHTML;
    const documentLossesCheck = document.getElementById(
        "settlement-benefit-election_1"
    );
    toggleDocumentedFieldsVisibility(documentLossesCheck.checked);
    toggleDocumentedLossesVisibilityOnCheck();
    setupDocumentedLossesValidation();
    setupTableButtonListeners();
    return claim;
}

function toggleDocumentedFieldsVisibility(visibility) {
    toggleElementVisibility(
        '[data-field-id="_documented-losses-attestation"]',
        visibility
    );
    toggleElementVisibility("#transactions", visibility);
    toggleElementVisibility("#documentation", visibility);
}

function toggleDocumentedLossesVisibilityOnCheck() {
    const documentedCheck = document.getElementById(
        "settlement-benefit-election_1"
    );
    const undocumentedCheck = document.getElementById(
        "settlement-benefit-election_2"
    );

    documentedCheck.addEventListener("change", () => {
        toggleDocumentedFieldsVisibility(true);
        setTimeout(() => {
            setupDocumentedLossesValidation();
        }, 100);
    });
    undocumentedCheck.addEventListener("change", () => {
        toggleDocumentedFieldsVisibility(false);
    });
}

/**
 * Sets up validation for documented losses to ensure total doesn't exceed limit
 */
function setupDocumentedLossesValidation() {
    // Create error container if it doesn't exist
    const transactionsSection = document.querySelector("#transactions");
    if (
        transactionsSection &&
        !document.getElementById(DOCUMENTED_LOSSES_ERROR_CONTAINER_ID)
    ) {
        const errorContainer = document.createElement("div");
        errorContainer.id = DOCUMENTED_LOSSES_ERROR_CONTAINER_ID;
        errorContainer.className = "text-danger mt-2";
        errorContainer.setAttribute("role", "alert");

        // Insert after the transactions table
        const table = transactionsSection.querySelector("table");
        if (table && table.parentNode) {
            table.parentNode.insertBefore(errorContainer, table.nextSibling);
        }
    }

    // Attach listeners to existing amount inputs
    attachDocumentedLossesListeners();

    // Perform initial validation
    validateDocumentedLosses();
}

/**
 * Sets up listeners for add/remove buttons in the transactions table
 */
function setupTableButtonListeners() {
    const addButton = document.querySelector("#add_transactions_1");
    const removeButton = document.querySelector("#remove_transactions_1");

    if (addButton) {
        addButton.addEventListener("click", () => {
            // Wait for new row to be added
            setTimeout(() => {
                attachDocumentedLossesListeners();
                validateDocumentedLosses();
            }, 100);
        });
    }

    if (removeButton) {
        removeButton.addEventListener("click", () => {
            // Wait for row to be removed
            setTimeout(() => {
                validateDocumentedLosses();
            }, 100);
        });
    }
}

/**
 * Attaches input listeners to all documented loss amount fields
 */
function attachDocumentedLossesListeners() {
    const inputs = document.querySelectorAll(
        DOCUMENTED_LOSSES_AMOUNT_INPUT_SELECTOR
    );
    inputs.forEach((input) => {
        const handler = () => validateDocumentedLosses();
        // Remove existing listeners to avoid duplicates
        input.removeEventListener("input", handler);
        input.removeEventListener("change", handler);
        // Add new listeners
        input.addEventListener("input", handler);
        input.addEventListener("change", handler);
    });
}

/**
 * Calculates the total of all documented losses
 * @returns {number} The total amount
 */
function getDocumentedLossesTotal() {
    const inputs = document.querySelectorAll(
        DOCUMENTED_LOSSES_AMOUNT_INPUT_SELECTOR
    );
    let total = 0;
    inputs.forEach((input) => {
        const value = parseFloat(String(input.value).replace(/[^0-9.\-]/g, ""));
        if (!isNaN(value)) {
            total += value;
        }
    });
    return total;
}

/**
 * Renders error message for documented losses validation
 * @param {string} message - The error message to display
 */
function renderDocumentedLossesError(message = "") {
    const container = document.getElementById(
        DOCUMENTED_LOSSES_ERROR_CONTAINER_ID
    );
    if (!container) return;
    container.textContent = message;
}

function toggleFileSelectRequired(required) {
    const fileSelect = document.getElementById("file-upload");
    fileSelect.required = required;
    fileSelect.classList.toggle("is-invalid", required);
    const fileDescription = document.getElementById("file-description");
    fileDescription.required = required;
    fileDescription.classList.toggle("is-invalid", required);
}

/**
 * Validates that documented losses don't exceed the limit
 * @returns {boolean} True if valid, false otherwise
 */
function validateDocumentedLosses() {
    const total = getDocumentedLossesTotal();
    const inputs = document.querySelectorAll(
        DOCUMENTED_LOSSES_AMOUNT_INPUT_SELECTOR
    );

    if (total > DOCUMENTED_LOSSES_LIMIT) {
        renderDocumentedLossesError(
            `Total documented losses cannot exceed $${DOCUMENTED_LOSSES_LIMIT.toLocaleString()}.`
        );
        // Add is-invalid class to all amount inputs
        inputs.forEach((input) => {
            input.classList.add("is-invalid");
        });
        return false;
    }

    // Clear error message and remove invalid classes
    renderDocumentedLossesError("");
    inputs.forEach((input) => {
        input.classList.remove("is-invalid");
    });
    return true;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    // custom code goes here
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    // Validate documented losses limit before submission
    if (!validateDocumentedLosses()) {
        claim.flags.valid = false;
    }
    const hasDocumentationRequirements = document.getElementById(
        "settlement-benefit-election_1"
    ).checked;
    if (hasDocumentationRequirements) {
        if (!hasFileUploads(claim)) {
            claim.flags.valid = false;
            claim.flags.uploadsRequired = true;
            toggleFileSelectRequired(true);
        } else {
            claim.flags.uploadsRequired = false;
            toggleFileSelectRequired(false);
        }
    }

    return claim;
}

function hasFileUploads(claim) {
    return (
        claim.uploads.length > 0 ||
        (claim.class_member?.claim_files &&
            claim.class_member.claim_files.length > 0)
    );
}

/**
 * Toggles visibility and handles required/data-validate attributes for input elements
 * @param {HTMLElement} element - The element to toggle visibility for
 * @param {boolean} isVisible - Whether the element should be visible
 */
function toggleElementVisibility(selector, isVisible = true) {
    const element = document.querySelector(selector);
    if (!element) {
        return;
    }
    element.classList.toggle("d-none", !isVisible);
    element.setAttribute("aria-hidden", isVisible ? "false" : "true");
    // Handle required and data-validate attributes for inputs and their children
    toggleInputValidation(element, isVisible);
}

/**
 * Recursively handles required and data-validate attributes for input elements
 * @param {HTMLElement} element - The element to process
 * @param {boolean} isVisible - Whether the element should be visible
 */
function toggleInputValidation(element, isVisible) {
    // If element is an input, handle its attributes
    if (["INPUT", "SELECT", "TEXTAREA"].includes(element.tagName)) {
        toggleFieldRequired(element, isVisible);
        return;
    }

    // Query for children that are input, select, or textarea elements
    const inputChildren = element.querySelectorAll("input, select, textarea");
    inputChildren.forEach((child) => {
        toggleFieldRequired(child, isVisible);
    });
}

function toggleFieldRequired(element, isVisible) {
    if (isVisible) {
        // Add required and data-validate attributes when visible
        element.setAttribute("required", "");
        element.setAttribute("data-validate", "required");
    } else {
        // Remove required and data-validate attributes when hidden
        element.removeAttribute("required");
        element.removeAttribute("data-validate");
    }
}
